/**
 * The sidebar, organised the way Gmail does it.
 *
 * IMAP hands us a flat list of folders -- Inbox, Archive, Drafts, Junk, Sent,
 * Trash, plus anything the server or the user made -- and showing all of them
 * with equal weight read as "five inboxes to check". Mail only ever ARRIVES in
 * one of them. So:
 *
 *   primary  Inbox, Starred, Sent, Drafts      (what people use every day)
 *   more     Archive, Spam, Trash, then custom (collapsed until asked for)
 *
 * Starred is a view, not a folder: the Inbox filtered to flagged messages,
 * using SnappyMail's own `is:flagged` search. It costs one IMAP SEARCH and no
 * new server code.
 *
 * Pure functions only -- no React, no API -- so it can be tested directly.
 */

import { folderForRole, isSelectable } from '../api/mail';
import type { Folder } from '../api/types';
import { folderLabel } from '../format';

export interface NavItem {
	/** Stable id for selection: the folder path, or "starred". */
	id: string;
	label: string;
	/** Monochrome glyph shown before the label. The label always carries the meaning. */
	icon: string;
	/** The IMAP folder the view lists. */
	folder: string;
	/** Search applied on top of the folder, for virtual views. */
	filter?: string;
	/** Unread count to show, when it is worth showing. */
	count?: number;
}

export interface Mailboxes {
	primary: NavItem[];
	more: NavItem[];
	/** Resolved role folders, for actions (archive, delete, spam, send). */
	roles: {
		inbox: string;
		sent: string | null;
		drafts: string | null;
		archive: string | null;
		junk: string | null;
		trash: string | null;
	};
}

/** App-data settings that name the account's special folders. */
export interface FolderSettings {
	SentFolder?: string;
	DraftsFolder?: string;
	TrashFolder?: string;
	JunkFolder?: string;
	ArchiveFolder?: string;
}

export const STARRED = 'starred';

export function buildMailboxes(all: Folder[], settings: FolderSettings = {}): Mailboxes {
	const folders = all.filter(isSelectable);

	const inbox = folderForRole(folders, 'inbox', ['INBOX']);
	const sent = folderForRole(folders, 'sent', ['Sent Items', 'Sent Messages', 'Sent Mail'], settings.SentFolder);
	const drafts = folderForRole(folders, 'drafts', [], settings.DraftsFolder);
	const archive = folderForRole(folders, 'archive', ['All Mail'], settings.ArchiveFolder);
	const junk = folderForRole(folders, 'junk', ['Spam', 'Junk E-mail', 'Bulk Mail'], settings.JunkFolder);
	const trash = folderForRole(folders, 'trash', ['Deleted Items', 'Deleted Messages', 'Bin'], settings.TrashFolder);

	const inboxName = inbox?.fullName ?? 'INBOX';
	const item = (folder: Folder, label: string, icon: string, count?: boolean): NavItem => ({
		id: folder.fullName,
		label,
		icon,
		folder: folder.fullName,
		count: count && folder.unreadEmails > 0 ? folder.unreadEmails : undefined
	});

	const primary: NavItem[] = [
		inbox
			? item(inbox, 'Inbox', '▣', true)
			: { id: inboxName, label: 'Inbox', icon: '▣', folder: inboxName },
		{ id: STARRED, label: 'Starred', icon: '★', folder: inboxName, filter: 'is:flagged' }
	];
	sent && primary.push(item(sent, 'Sent', '➤'));
	drafts && primary.push(item(drafts, 'Drafts', '✎'));

	const more: NavItem[] = [];
	archive && more.push(item(archive, 'Archive', '⬒'));
	// Spam keeps its count: mail landing there by mistake is worth noticing.
	junk && more.push(item(junk, 'Spam', '⚠', true));
	trash && more.push(item(trash, 'Trash', '⌫'));

	const taken = new Set([inbox, sent, drafts, archive, junk, trash].filter(Boolean).map(f => f!.fullName));
	folders
		.filter(folder => !taken.has(folder.fullName))
		.sort((a, b) => a.fullName.localeCompare(b.fullName))
		.forEach(folder => more.push(item(folder, folderLabel(folder.fullName, folder.delimiter), '▢', true)));

	return {
		primary,
		more,
		roles: {
			inbox: inboxName,
			sent: sent?.fullName ?? null,
			drafts: drafts?.fullName ?? null,
			archive: archive?.fullName ?? null,
			junk: junk?.fullName ?? null,
			trash: trash?.fullName ?? null
		}
	};
}

/** Finds a view by id, falling back to the Inbox. */
export function findView(boxes: Mailboxes, id: string): NavItem {
	return [...boxes.primary, ...boxes.more].find(item => item.id === id) ?? boxes.primary[0];
}
